/**
 * Artifact-library client (task #7 · Phase 7a). A signed-in user manages their
 * own GENERATED artifacts (documents produced in chat via create_artifact):
 * list / get / rename / remove. Mirrors the personalKbApi fetch + authHeaders
 * idiom. Routes 404 when FEATURE_ARTIFACTS is off, 401 without a signed-in user.
 */
import type { ArtifactSurface } from "@shared/types";
import { authHeaders } from "./auth";

/** Human label for each artifact surface (badge + canvas subtitle). */
export const SURFACE_LABEL: Record<ArtifactSurface, string> = {
  doc: "文档",
  report: "报告",
  table: "表格",
};

/** One historical version of an artifact (newest LAST in `versions`). */
export interface ArtifactVersion {
  content: string;
  title: string;
  /** ISO timestamp of this version. */
  createdAt: string;
}

/** A Library row (list shape): the server omits per-version bodies to stay light. */
export interface ArtifactSummary {
  id: string;
  title: string;
  /** Output surface (markdown variant): doc / report / table. */
  surface: ArtifactSurface;
  /** Phase 7b: promoted into personal-KB knowledge. Always false in 7a. */
  promoted: boolean;
  /** The session that produced it (provenance). Absent if unknown. */
  originSessionId?: string;
  /** ISO creation timestamp. */
  createdAt: string;
  /** ISO timestamp of the latest change (create / regenerate / rename). */
  updatedAt: string;
  /** How many versions exist (a regenerate appends one). */
  versionCount: number;
}

/** Full artifact (get shape): current content + version history. */
export interface ArtifactDetail extends Omit<ArtifactSummary, "versionCount"> {
  content: string;
  versions: ArtifactVersion[];
  /** P2: the read-only share token (owner view). Absent when not shared. */
  shareToken?: string;
}

/** Public read-only shape (GET /api/artifacts/shared/:token): viewable fields only. */
export interface SharedArtifact {
  title: string;
  surface: ArtifactSurface;
  content: string;
}

/** A failed artifact call with a user-facing reason derived from the HTTP status. */
export class ArtifactError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ArtifactError";
  }
}

export const artifactApi = {
  /** The caller's artifacts, newest first. Empty on any failure. */
  list: async (): Promise<ArtifactSummary[]> => {
    const res = await fetch("/api/artifacts", { headers: authHeaders() });
    if (!res.ok) return [];
    return ((await res.json()) as { artifacts: ArtifactSummary[] }).artifacts;
  },

  /** One artifact incl. current content + versions; null when absent. */
  get: async (id: string): Promise<ArtifactDetail | null> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}`, { headers: authHeaders() });
    if (res.status === 404) return null;
    if (!res.ok) throw new ArtifactError("打不开这份文档，稍后再试。", res.status);
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /** Rename an artifact; returns the updated row. */
  rename: async (id: string, title: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) {
      throw new ArtifactError(res.status === 400 ? "名字不能为空。" : "改名没成功，稍后再试。", res.status);
    }
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /**
   * Inline edit (P1): save edited markdown as a new version. `title` is optional
   * (kept unchanged when omitted). Returns the updated artifact (bumped version).
   */
  saveContent: async (id: string, content: string, title?: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/content`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(title !== undefined ? { content, title } : { content }),
    });
    if (!res.ok) {
      const msg =
        res.status === 400 ? "内容不能为空。" : res.status === 413 ? "内容太长，无法保存。" : "保存没成功，稍后再试。";
      throw new ArtifactError(msg, res.status);
    }
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /**
   * AI polish (P1): server-side rewrite of the DRAFT markdown. Returns the
   * polished markdown WITHOUT committing — the caller shows it in the editor with
   * an undo, then 保存 (saveContent) commits. Bypasses the chat 4000-char cap.
   */
  polish: async (id: string, content: string): Promise<string> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/polish`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      throw new ArtifactError(
        res.status === 413 ? "文档较长，整篇 AI 润色暂不支持，可分段手动编辑。" : "润色没成功，稍后再试。",
        res.status,
      );
    }
    return ((await res.json()) as { polished: string }).polished;
  },

  /** Remove one artifact. */
  remove: async (id: string): Promise<boolean> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    return res.ok;
  },

  /** 7b: promote an artifact into knowledge (auto-grounds future chats). */
  promote: async (id: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/promote`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!res.ok) {
      throw new ArtifactError(
        res.status === 409 ? "先在「我的资料」里开启资料库功能，才能加入知识。" : "加入知识没成功，稍后再试。",
        res.status,
      );
    }
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /** 7b: remove an artifact from knowledge (drops its KB copy). */
  unpromote: async (id: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/promote`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new ArtifactError("移出知识没成功，稍后再试。", res.status);
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /** P2: enable read-only sharing; returns the artifact with its shareToken. */
  share: async (id: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/share`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!res.ok) throw new ArtifactError("生成分享链接没成功，稍后再试。", res.status);
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /** P2: 关闭分享 — revoke the link (clears shareToken); returns the updated artifact. */
  unshare: async (id: string): Promise<ArtifactDetail> => {
    const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/share`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new ArtifactError("关闭分享没成功，稍后再试。", res.status);
    return ((await res.json()) as { artifact: ArtifactDetail }).artifact;
  },

  /** P2 viewer: fetch a shared artifact by token (public, no auth); null when the
   *  link is invalid / revoked. */
  getShared: async (token: string): Promise<SharedArtifact | null> => {
    const res = await fetch(`/api/artifacts/shared/${encodeURIComponent(token)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new ArtifactError("打不开这个分享链接，稍后再试。", res.status);
    return ((await res.json()) as { artifact: SharedArtifact }).artifact;
  },
};

/** Build the public read-only URL for a share token (viewer route). */
export function shareUrl(token: string): string {
  return `${window.location.origin}/shared/artifact/${token}`;
}
